import React, { useState, useEffect, useContext } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';

import { FirebaseContext } from '../App';
import { firebaseAppId } from '../configs/firebaseConfig';

const Reports = () => {
    const { db, userId } = useContext(FirebaseContext);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i); // Current year +/- 2 years

    useEffect(() => {
        if (!db || !userId) return;

        setLoading(true);
        const transactionsColRef = collection(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`);
        const unsubscribe = onSnapshot(transactionsColRef, (snapshot) => {
            const fetchedTransactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTransactions(fetchedTransactions);
            setLoading(false);
        }, (error) => {
            console.error("レポートデータの取得エラー:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, userId]);

    // Filter transactions by selected year and month
    const filteredTransactions = transactions.filter(t => {
        const transactionDate = t.date?.toDate();
        if (!transactionDate) return false;
        const yearMatch = transactionDate.getFullYear() === selectedYear;
        const monthMatch = selectedMonth === 0 || transactionDate.getMonth() + 1 === selectedMonth;
        return yearMatch && monthMatch;
    });

    // --- 損益計算書 (簡易版) ---
    const incomeByMonth = {};
    const expenseByMonth = {};
    const incomeByCategory = {};
    const expenseByCategory = {};

    filteredTransactions.forEach(t => {
        const month = t.date?.toDate().getMonth() + 1;
        if (month) {
            if (t.type === '収入') {
                incomeByMonth[month] = (incomeByMonth[month] || 0) + t.amount;
                incomeByCategory[t.category] = (incomeByCategory[t.category] || 0) + t.amount;
            } else {
                expenseByMonth[month] = (expenseByMonth[month] || 0) + t.amount;
                expenseByCategory[t.category] = (expenseByCategory[t.category] || 0) + t.amount;
            }
        }
    });

    const totalIncome = Object.values(incomeByCategory).reduce((acc, val) => acc + val, 0);
    const totalExpense = Object.values(expenseByCategory).reduce((acc, val) => acc + val, 0);
    const netProfit = totalIncome - totalExpense;

    // --- 貸借対照表 (簡易版 - 現金・預金のみ) ---
    const calculateBalance = (account) => {
        let balance = 0;
        transactions.forEach(t => {
            if (t.paymentMethod === account) {
                if (t.type === '収入') {
                    balance += t.amount;
                } else if (t.type === '支出') {
                    balance -= t.amount;
                }
            } else if (account === '売掛金' && t.type === '収入' && t.paymentMethod === '売掛金') {
                balance += t.amount;
            } else if (account === '買掛金' && t.type === '支出' && t.paymentMethod === '買掛金') {
                balance += t.amount;
            }
        });
        return balance;
    };

    const cashBalance = calculateBalance('現金');
    const bankBalance = calculateBalance('普通預金');
    const receivablesBalance = calculateBalance('売掛金');
    const payablesBalance = calculateBalance('買掛金');

    const totalAssets = cashBalance + bankBalance + receivablesBalance;
    const totalLiabilities = Math.abs(payablesBalance);

    const ownerEquity = totalAssets - totalLiabilities;

    return (
        <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 border-b pb-4">レポート</h2>

            {loading ? (
                <div className="text-center text-gray-600">レポートデータを読み込み中...</div>
            ) : (
                <>
                    {/* 期間選択 - モバイルでは縦積み、PCでは横並び */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="shadow border rounded-lg py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400 w-full sm:w-auto"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}年</option>
                            ))}
                        </select>
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                            className="shadow border rounded-lg py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400 w-full sm:w-auto"
                        >
                            <option value={0}>年間（全期間）</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month}>{month}月</option>
                            ))}
                        </select>
                    </div>

                    {/* 損益計算書 */}
                    <div className="mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">損益計算書 ({selectedYear}年 {selectedMonth === 0 ? '年間' : `${selectedMonth}月`})</h3>
                        <div className="bg-gray-50 rounded-lg p-6 mb-4">
                            <h4 className="text-xl font-bold text-gray-700 mb-3">収入</h4>
                            {Object.keys(incomeByCategory).length === 0 ? (
                                <p className="text-gray-600">収入がありません。</p>
                            ) : (
                                <ul className="space-y-2">
                                    {Object.entries(incomeByCategory).map(([category, amount]) => (
                                        <li key={category} className="flex justify-between items-center text-gray-700">
                                            <span>{category}</span>
                                            <span className="font-medium">¥{amount.toLocaleString()}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="flex justify-between items-center text-lg font-bold text-blue-700 mt-4 pt-4 border-t border-gray-200">
                                <span>合計収入</span>
                                <span>¥{totalIncome.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6">
                            <h4 className="text-xl font-bold text-gray-700 mb-3">支出</h4>
                            {Object.keys(expenseByCategory).length === 0 ? (
                                <p className="text-gray-600">支出がありません。</p>
                            ) : (
                                <ul className="space-y-2">
                                    {Object.entries(expenseByCategory).map(([category, amount]) => (
                                        <li key={category} className="flex justify-between items-center text-gray-700">
                                            <span>{category}</span>
                                            <span className="font-medium">¥{amount.toLocaleString()}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="flex justify-between items-center text-lg font-bold text-red-700 mt-4 pt-4 border-t border-gray-200">
                                <span>合計支出</span>
                                <span>¥{totalExpense.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="bg-blue-100 rounded-lg p-6 mt-6 flex justify-between items-center">
                            <h4 className="text-2xl font-bold text-blue-800">純利益</h4>
                            <p className="text-3xl font-bold text-blue-800">¥{netProfit.toLocaleString()}</p>
                        </div>
                    </div>

                    {/* 貸借対照表 (簡易版) - モバイルでは1列、PCでは2列 */}
                    <div className="mt-12">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">貸借対照表 (簡易版)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
                                <h4 className="text-xl font-bold text-gray-700 mb-3">資産の部</h4>
                                <ul className="space-y-2">
                                    <li className="flex justify-between text-gray-700"><span>現金</span><span className="font-medium">¥{cashBalance.toLocaleString()}</span></li>
                                    <li className="flex justify-between text-gray-700"><span>普通預金</span><span className="font-medium">¥{bankBalance.toLocaleString()}</span></li>
                                    <li className="flex justify-between text-gray-700"><span>売掛金 (未回収)</span><span className="font-medium">¥{receivablesBalance.toLocaleString()}</span></li>
                                </ul>
                                <div className="flex justify-between text-lg font-bold text-blue-700 mt-4 pt-4 border-t border-gray-200">
                                    <span>資産合計</span>
                                    <span>¥{totalAssets.toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
                                <h4 className="text-xl font-bold text-gray-700 mb-3">負債の部 / 純資産の部</h4>
                                <h5 className="text-lg font-semibold text-gray-700 mb-2">負債</h5>
                                <ul className="space-y-2">
                                    <li className="flex justify-between text-gray-700"><span>買掛金 (未払い)</span><span className="font-medium">¥{payablesBalance.toLocaleString()}</span></li>
                                </ul>
                                <div className="flex justify-between text-lg font-bold text-red-700 mt-4 pt-4 border-t border-gray-200">
                                    <span>負債合計</span>
                                    <span>¥{totalLiabilities.toLocaleString()}</span>
                                </div>
                                <h5 className="text-lg font-semibold text-gray-700 mt-6 mb-2">純資産</h5>
                                <div className="flex justify-between text-lg font-bold text-green-700 pt-2 border-t border-gray-200">
                                    <span>純資産合計</span>
                                    <span>¥{ownerEquity.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Reports;
